import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../components/Pagination';
import {
  createAdminPatient,
  listAdminPatients,
  patchAdminPatient
} from '../api/adminPatientsApi';
import { formatDateTime, formatPatientName, normalizeUiLocale } from '../utils/locale';

const emptyPatientFilters = {
  mrn: '',
  phone: '',
  first_name: '',
  last_name: ''
};

const emptyPatientForm = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: 'female',
  email: '',
  phone: '',
  phone_region: '',
  address: '',
  mrn: '',
  patient_id: '',
  suppress_general_history_context: false
};

const getRequestErrorMessage = (error, fallback) => {
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
};

const formatDateForInput = (value) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value).slice(0, 10);
  }
  return parsed.toISOString().slice(0, 10);
};

const normalizePatientPayload = (form) => {
  return {
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    date_of_birth: `${form.date_of_birth}T00:00:00Z`,
    gender: form.gender,
    email: form.email.trim(),
    phone: form.phone.trim(),
    phone_e164: '',
    phone_national: '',
    phone_raw: form.phone.trim(),
    phone_region: form.phone_region.trim(),
    address: form.address.trim(),
    mrn: form.mrn.trim(),
    patient_id: form.patient_id.trim(),
    suppress_general_history_context: Boolean(form.suppress_general_history_context)
  };
};

const AdminPatientManagement = () => {
  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const [patientFilters, setPatientFilters] = useState(emptyPatientFilters);
  const [appliedPatientFilters, setAppliedPatientFilters] = useState(emptyPatientFilters);
  const [patients, setPatients] = useState([]);
  const [patientsTotal, setPatientsTotal] = useState(0);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [refreshTick, setRefreshTick] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState(null);
  const [form, setForm] = useState(emptyPatientForm);
  const [saving, setSaving] = useState(false);
  const [rowActionKey, setRowActionKey] = useState('');

  const selectedPatient = useMemo(
    () => patients.find((patient) => patient.id === editingPatientId) || null,
    [patients, editingPatientId]
  );

  const totalPages = Math.max(1, Math.ceil(patientsTotal / pageSize));
  const activeRowAction = Boolean(rowActionKey);

  useEffect(() => {
    let cancelled = false;

    const fetchPatients = async () => {
      try {
        setPatientsLoading(true);
        setError('');
        const offset = (currentPage - 1) * pageSize;
        const data = await listAdminPatients({
          ...appliedPatientFilters,
          limit: pageSize,
          offset
        });

        if (cancelled) {
          return;
        }

        setPatients(Array.isArray(data?.patients) ? data.patients : []);
        setPatientsTotal(Number.isFinite(data?.total) ? data.total : 0);
      } catch (requestError) {
        console.error('Failed to load admin patients:', requestError);
        if (!cancelled) {
          setError(getRequestErrorMessage(
            requestError,
            t('patients.adminLoadError')
          ));
        }
      } finally {
        if (!cancelled) {
          setPatientsLoading(false);
        }
      }
    };

    fetchPatients();
    return () => {
      cancelled = true;
    };
  }, [appliedPatientFilters, currentPage, pageSize, refreshTick, t]);

  const refreshPatients = () => {
    setRefreshTick((prev) => prev + 1);
  };

  const handlePatientFilterChange = (field, value) => {
    setPatientFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyPatientFilters = (event) => {
    event.preventDefault();
    const trimmedFilters = Object.entries(patientFilters).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: value.trim()
    }), {});
    setCurrentPage(1);
    setAppliedPatientFilters(trimmedFilters);
  };

  const resetPatientFilters = () => {
    setPatientFilters(emptyPatientFilters);
    setAppliedPatientFilters(emptyPatientFilters);
    setCurrentPage(1);
  };

  const beginCreate = () => {
    setError('');
    setNotice('');
    setEditingPatientId(null);
    setForm(emptyPatientForm);
    setShowForm(true);
  };

  const beginEdit = (patient) => {
    setError('');
    setNotice('');
    setEditingPatientId(patient.id);
    setForm({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      date_of_birth: formatDateForInput(patient.date_of_birth),
      gender: patient.gender || 'female',
      email: patient.email || '',
      phone: patient.phone_raw || patient.phone_e164 || patient.phone || '',
      phone_region: patient.phone_region || '',
      address: patient.address || '',
      mrn: patient.mrn || '',
      patient_id: patient.patient_id || '',
      suppress_general_history_context: Boolean(patient.suppress_general_history_context)
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingPatientId(null);
    setForm(emptyPatientForm);
    setShowForm(false);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!form.first_name.trim() || !form.date_of_birth || !form.gender) {
      setError(t('patients.adminRequiredFields'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setNotice('');

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = normalizePatientPayload(form);
      if (editingPatientId) {
        await patchAdminPatient(editingPatientId, payload);
        setNotice(t('patients.adminUpdateSuccess'));
      } else {
        await createAdminPatient(payload);
        setNotice(t('patients.adminCreateSuccess'));
      }
      resetForm();
      refreshPatients();
    } catch (requestError) {
      console.error('Failed to save admin patient:', requestError);
      setError(getRequestErrorMessage(
        requestError,
        t('patients.adminSaveError')
      ));
    } finally {
      setSaving(false);
    }
  };

  const updatePatientRow = (updatedPatient) => {
    setPatients((current) => current.map((patient) => (
      patient.id === updatedPatient.id ? updatedPatient : patient
    )));
  };

  const handleToggleSuppressHistory = async (patient) => {
    const nextValue = !patient.suppress_general_history_context;
    try {
      setRowActionKey(`toggle-${patient.id}`);
      setError('');
      const updatedPatient = await patchAdminPatient(patient.id, {
        suppress_general_history_context: nextValue
      });
      updatePatientRow(updatedPatient);
      setNotice(nextValue
        ? t('patients.adminIgnoreHistoryOn')
        : t('patients.adminIgnoreHistoryOff'));
    } catch (requestError) {
      console.error('Failed to toggle patient history context:', requestError);
      setError(getRequestErrorMessage(
        requestError,
        t('patients.adminHistoryUpdateError')
      ));
    } finally {
      setRowActionKey('');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header admin-header-panel">
        <div className="page-title-block">
          <h1 className="page-title">{t('dashboard.patientManagement')}</h1>
        </div>
        <div className="page-actions">
          <button type="button" className="theme-button theme-button--primary" onClick={beginCreate}>
            {t('patients.adminCreate')}
          </button>
          <button type="button" className="theme-button theme-button--ghost" onClick={refreshPatients} disabled={patientsLoading}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error && !showForm && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="status-banner status-banner--success">
          <span>{notice}</span>
        </div>
      )}

      <section className="surface-panel admin-panel">
        <div className="surface-panel__body stack">
          <div className="admin-section-header">
            <div className="admin-stat-strip">
              <span className="status-pill status-pill--cool">
                {t('patients.adminTotal', { count: patientsTotal })}
              </span>
            </div>
          </div>

          <form onSubmit={applyPatientFilters} className="debug-patient-filter-row admin-patient-filter-row">
            <div className="field">
              <label className="field__label" htmlFor="admin-patient-filter-mrn">{t('patients.mrn')}</label>
              <input
                id="admin-patient-filter-mrn"
                className="theme-input"
                type="text"
                value={patientFilters.mrn}
                onChange={(event) => handlePatientFilterChange('mrn', event.target.value)}
                placeholder={t('debugDemo.mrnPlaceholder')}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="admin-patient-filter-phone">{t('debugDemo.phone')}</label>
              <input
                id="admin-patient-filter-phone"
                className="theme-input"
                type="text"
                value={patientFilters.phone}
                onChange={(event) => handlePatientFilterChange('phone', event.target.value)}
                placeholder={t('debugDemo.phonePlaceholder')}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="admin-patient-filter-first-name">{t('debugDemo.firstName')}</label>
              <input
                id="admin-patient-filter-first-name"
                className="theme-input"
                type="text"
                value={patientFilters.first_name}
                onChange={(event) => handlePatientFilterChange('first_name', event.target.value)}
                placeholder={t('debugDemo.firstNamePlaceholder')}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="admin-patient-filter-last-name">{t('debugDemo.lastName')}</label>
              <input
                id="admin-patient-filter-last-name"
                className="theme-input"
                type="text"
                value={patientFilters.last_name}
                onChange={(event) => handlePatientFilterChange('last_name', event.target.value)}
                placeholder={t('debugDemo.lastNamePlaceholder')}
              />
            </div>

            <button type="submit" className="theme-button theme-button--primary" disabled={patientsLoading}>
              {t('common.search')}
            </button>
            <button type="button" className="theme-button theme-button--ghost" onClick={resetPatientFilters}>
              {t('common.reset')}
            </button>
          </form>

          {patientsLoading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : patients.length === 0 ? (
            <div className="empty-state">
              <h3>{t('debugDemo.noPatients')}</h3>
              <p>{t('debugDemo.noPatientsHint')}</p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table admin-patient-table">
                <thead>
                  <tr>
                    <th>{t('debugDemo.patientColumnId')}</th>
                    <th>{t('debugDemo.patientColumnName')}</th>
                    <th>{t('patients.mrn')}</th>
                    <th>{t('debugDemo.phone')}</th>
                    <th>{t('debugDemo.patientColumnProfile')}</th>
                    <th>{t('patients.adminIgnoreHistory')}</th>
                    <th>{t('debugDemo.patientColumnCreatedAt')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => {
                    const toggleKey = `toggle-${patient.id}`;
                    const isToggleLoading = rowActionKey === toggleKey;

                    return (
                      <tr key={patient.id}>
                        <td>
                          <div className="table-title">#{patient.id}</div>
                          {patient.patient_id && (
                            <div className="table-meta">{patient.patient_id}</div>
                          )}
                        </td>
                        <td>
                          <div className="table-title">
                            <bdi dir="auto">{formatPatientName(patient, locale, t('common.unknown'))}</bdi>
                          </div>
                          <div className="table-meta">{patient.email || '-'}</div>
                        </td>
                        <td>{patient.mrn || '-'}</td>
                        <td>
                          <div>{patient.phone_e164 || patient.phone || '-'}</div>
                          {patient.phone_region && <div className="table-meta">{patient.phone_region}</div>}
                        </td>
                        <td>
                          <div className="table-title">{patient.gender || '-'}</div>
                          <div className="table-meta">
                            {Number.isFinite(patient.age) ? t('debugDemo.patientAge', { age: patient.age }) : t('common.unknown')}
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`admin-toggle ${patient.suppress_general_history_context ? 'is-on' : ''}`}
                            onClick={() => handleToggleSuppressHistory(patient)}
                            disabled={activeRowAction}
                            aria-pressed={Boolean(patient.suppress_general_history_context)}
                          >
                            <span className="admin-toggle__track">
                              <span className="admin-toggle__thumb" />
                            </span>
                            <span className="admin-toggle__label">
                              {isToggleLoading
                                ? t('common.submitting')
                                : patient.suppress_general_history_context
                                  ? t('patients.adminEnabled')
                                  : t('patients.adminDisabled')}
                            </span>
                          </button>
                        </td>
                        <td>{formatDateTime(patient.created_at, locale) || '-'}</td>
                        <td>
                          <div className="table-actions admin-patient-actions">
                            <button
                              type="button"
                              className="theme-button theme-button--ghost theme-button--compact"
                              onClick={() => beginEdit(patient)}
                              disabled={activeRowAction}
                            >
                              {t('common.edit')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={patientsTotal}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </div>
      </section>

      {showForm && (
        <div className="dialog-backdrop">
          <div
            className="dialog-shell dialog-shell--medium admin-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-patient-form-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="page-title-block">
              <h2 id="admin-patient-form-title" className="dialog-shell__title">
                {editingPatientId
                  ? formatPatientName(selectedPatient || {}, locale, t('patients.adminEdit'))
                  : t('patients.adminCreate')}
              </h2>
            </div>

            {error && (
              <div className="status-banner status-banner--error">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="stack">
              <div className="form-grid">
                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-first-name">{t('debugDemo.firstName')}</label>
                  <input
                    id="admin-patient-first-name"
                    className="theme-input"
                    type="text"
                    value={form.first_name}
                    onChange={(event) => handleFieldChange('first_name', event.target.value)}
                    autoFocus
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-last-name">{t('debugDemo.lastName')}</label>
                  <input
                    id="admin-patient-last-name"
                    className="theme-input"
                    type="text"
                    value={form.last_name}
                    onChange={(event) => handleFieldChange('last_name', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-dob">{t('patients.dateOfBirth')}</label>
                  <input
                    id="admin-patient-dob"
                    className="theme-input"
                    type="date"
                    value={form.date_of_birth}
                    onChange={(event) => handleFieldChange('date_of_birth', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-gender">{t('patients.gender')}</label>
                  <select
                    id="admin-patient-gender"
                    className="theme-select"
                    value={form.gender}
                    onChange={(event) => handleFieldChange('gender', event.target.value)}
                  >
                    <option value="female">{t('patients.genderFemale')}</option>
                    <option value="male">{t('patients.genderMale')}</option>
                    <option value="other">{t('patients.genderOther')}</option>
                  </select>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-mrn">{t('patients.mrn')}</label>
                  <input
                    id="admin-patient-mrn"
                    className="theme-input"
                    type="text"
                    value={form.mrn}
                    onChange={(event) => handleFieldChange('mrn', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-patient-id">{t('patients.patientId')}</label>
                  <input
                    id="admin-patient-patient-id"
                    className="theme-input"
                    type="text"
                    value={form.patient_id}
                    onChange={(event) => handleFieldChange('patient_id', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-phone">{t('debugDemo.phone')}</label>
                  <input
                    id="admin-patient-phone"
                    className="theme-input"
                    type="text"
                    value={form.phone}
                    onChange={(event) => handleFieldChange('phone', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-phone-region">{t('patients.phoneRegion')}</label>
                  <input
                    id="admin-patient-phone-region"
                    className="theme-input"
                    type="text"
                    value={form.phone_region}
                    onChange={(event) => handleFieldChange('phone_region', event.target.value.toUpperCase())}
                    placeholder="CN"
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-email">Email</label>
                  <input
                    id="admin-patient-email"
                    className="theme-input"
                    type="email"
                    value={form.email}
                    onChange={(event) => handleFieldChange('email', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="admin-patient-address">{t('patients.address')}</label>
                  <input
                    id="admin-patient-address"
                    className="theme-input"
                    type="text"
                    value={form.address}
                    onChange={(event) => handleFieldChange('address', event.target.value)}
                  />
                </div>
              </div>

              <label className="admin-checkbox-field">
                <input
                  type="checkbox"
                  checked={form.suppress_general_history_context}
                  onChange={(event) => handleFieldChange('suppress_general_history_context', event.target.checked)}
                />
                <span>{t('patients.adminIgnoreHistoryHint')}</span>
              </label>

              <div className="dialog-shell__actions">
                <button type="button" className="theme-button theme-button--ghost" onClick={resetForm} disabled={saving}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="theme-button theme-button--primary" disabled={saving}>
                  {saving ? t('common.submitting') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPatientManagement;
